import * as RadixPopover from '@radix-ui/react-popover';
import type { ComponentType, MouseEvent, ReactNode } from 'react';
import { createElement, useState } from 'react';

type PopoverOnItemClick = (event?: MouseEvent<HTMLElement>) => void;

export interface PopoverContentComponentProps {
  onItemClick: PopoverOnItemClick;
}

interface PopoverPropsBase {
  children: ReactNode;
  disabled?: boolean;
  /**
   * Prevents propagation of the trigger click event
   */
  stopPropagation?: boolean;

  triggerAsChild?: boolean;
  side?: RadixPopover.PopoverContentProps['side'];
  sideOffset?: RadixPopover.PopoverContentProps['sideOffset'];
  align?: RadixPopover.PopoverContentProps['align'];
  alignOffset?: RadixPopover.PopoverContentProps['alignOffset'];

  onOpenChange?: (open: boolean) => void;
  onOpenAutoFocus?: RadixPopover.PopoverContentProps['onOpenAutoFocus'];
}

type PopoverProps<T extends Record<string, unknown> = Record<string, never>> =
  | (PopoverPropsBase & {
      content: ReactNode;
      contentProps?: never;
    })
  | (PopoverPropsBase & {
      content: ComponentType<PopoverContentComponentProps & T>;
      contentProps?: T;
    });

export function Popover<
  T extends Record<string, unknown> = Record<string, never>,
>({
  content,
  contentProps,
  children,
  disabled,
  stopPropagation,
  triggerAsChild,
  side = 'right',
  sideOffset = 5,
  align,
  alignOffset,
  onOpenChange,
  onOpenAutoFocus,
}: PopoverProps<T>) {
  const [isOpened, setIsOpened] = useState(false);

  const closePopover = () => {
    setIsOpened(false);
  };

  const triggerOnClick = (e: MouseEvent<HTMLElement>) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
  };

  const ContentComponent =
    typeof content === 'function'
      ? createElement(content, {
          ...contentProps,
          onItemClick: closePopover,
        } as PopoverContentComponentProps & T)
      : content;

  const handleOpenChange = (open: boolean) => {
    setIsOpened(open);
    onOpenChange?.(open);
  };

  return (
    <RadixPopover.Root open={isOpened} onOpenChange={handleOpenChange}>
      <RadixPopover.Trigger
        disabled={disabled}
        onClick={triggerOnClick}
        asChild={triggerAsChild}
      >
        {children}
      </RadixPopover.Trigger>
      {!triggerAsChild ? <RadixPopover.Anchor /> : null}
      <RadixPopover.Portal>
        <RadixPopover.Content
          side={side}
          sideOffset={sideOffset}
          align={align}
          alignOffset={alignOffset}
          onOpenAutoFocus={onOpenAutoFocus}
          className="z-50"
          collisionPadding={8}
        >
          {ContentComponent}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}

Popover.displayName = 'Popover';
